import { localStorageService } from './LocalStorageService';
import { pageDataService } from './PageDataService';

export function isCreditMonitoringUIEnabled() {
  return !!pageDataService.getFlightSetting().creditMonitoringUIEnabled;
}

export function isCMFamilyUIEnabled(): boolean {
  return (
    !!pageDataService.getFlightSetting().creditMonitoringFamilyUIEnabled ||
    (!pageDataService.isProdEnvironment() && localStorageService.getItem('creditMonitoringFamilyUIEnabled') === 'true')
  );
}

export function isCreditMonitoringApiEnabled() {
  return !!pageDataService.getFlightSetting().creditMonitoringApi;
}

export function isCreditMonitoringRecommendationsEnabled() {
  return !!pageDataService.getFlightSetting().creditMonitoringRecommendations;
}

export function isSecureConnectionApiEnabled() {
  return !!pageDataService.getFlightSetting().secureConnectionApi;
}

export function isTrustedNetworkApiEnabled() {
  return !!pageDataService.getFlightSetting().trustedNetworkApi;
}

export function isMSAImportEnabled() {
  return !!pageDataService.getFlightSetting().showMsaImportButton;
}

export function isFamilySettingEnabled(): boolean {
  return !!pageDataService.getFlightSetting().familySetting;
}

export function isPrivacySettingEnabled(): boolean {
  return !!pageDataService.getFlightSetting().privacySetting;
}

export function isNotificationSettingEnabled(): boolean {
  return pageDataService.isDevEnvironment() || !!pageDataService.getFlightSetting().notificationSetting;
}

export function isSecurityScoreFeatureEnabled(): boolean {
  return !!pageDataService.getFlightSetting().securityScore;
}

export function isSandboxEnabled(): boolean {
  return !!pageDataService.getFlightSetting().sandbox;
}

export function isDeviceDetailsPageEnabled(): boolean {
  return !!pageDataService.getFlightSetting().deviceDetailsPage;
}

export function isITPSupported(): boolean {
  return !!pageDataService.getFlightSetting().identityMonitoring && pageDataService.isITPSupported();
}

export function isITPFamilyApiEnabled(): boolean {
  return !!pageDataService.getFlightSetting().identityTheftMonitoringFamilyApi;
}

export function isRemoveITPSubscriptionEnabled(): boolean {
  return !!pageDataService.getFlightSetting().removeITPSubscription;
}

export function isAddSSNEnabled(): boolean {
  return !!pageDataService.getFlightSetting().addSSN;
}

export function isDeviceAlertWebViewEnabled(): boolean {
  return !!pageDataService.getFlightSetting().deviceAlertWebView;
}

export function isAlertsPageEnabled(): boolean {
  return !!pageDataService.getFlightSetting().alertsPageEnabled;
}

export function englishLocaleOnly(): boolean {
  return !!pageDataService.getFlightSetting().useEnglishLocaleOnly;
}

export function isLicenseRequired(): boolean {
  return (
    !!pageDataService.getFlightSetting().licenseRequired ||
    (!pageDataService.isProdEnvironment() && localStorageService.getItem('isLicenseRequired') === 'true')
  );
}

export function isWellKnownChangePasswordEnabled(): boolean {
  return !!pageDataService.getFlightSetting().wellKnownChangePassword;
}

export function isDimeEnabled(): boolean {
  return !!pageDataService.getFlightSetting().enableDime;
}

export function shouldSetupMsalBroker(): boolean {
  return isDimeEnabled() && !!pageDataService.getFlightSetting().enableDimeMsalBroker;
}

export function isGroupItmBreachesEnabled(): boolean {
  return !!pageDataService.getFlightSetting().groupItmBreachesAlerts;
}

export function shouldRedirectToItmOnboarding(): boolean {
  const skipOnboarding = localStorageService.getOnboardingSkipToDashboard();
  return isITPSupported() && !skipOnboarding;
}

export function isBreachBulkActionEnabled(): boolean {
  return !!pageDataService.getFlightSetting().breachBulkAction;
}

export function shouldPaywallShowTrial(): boolean {
  return !!pageDataService.getFlightSetting().webPaywallShowTrial;
}

export function shouldUseBreachBulkUpdateAPI(): boolean {
  return !!pageDataService.getFlightSetting().useBreachBulkUpdateAPI;
}

export function shouldUseCodeInputV2(): boolean {
  return true;
}

export function isClarityEnabled(): boolean {
  return !pageDataService.isWebView() && !!pageDataService.getFlightSetting().enableClarity;
}

export function getClarityProjectId(): string {
  return pageDataService.getFlightSetting().clarityProjectId || '';
}

export function shouldShowRewardActivities(): boolean {
  return !!pageDataService.getFlightSetting().showRewardActivities;
}

export function isOneTimeScanEnabled(): boolean {
  return !!pageDataService.getFlightSetting().oneTimeCyberAgentScanApi;
}

export function shouldShowUnmonitoredTypes(): boolean {
  return !!pageDataService.getFlightSetting().showUnmonitoredTypes;
}

// Checks whether to use the collapsible component for monitoring/unmonitoring types
export function shouldUseCollapsibleMonitoringType(): boolean {
  return shouldShowUnmonitoredTypes();
}

export function isCMApiProtectionEnabled(): boolean {
  return !!pageDataService.getFlightSetting().cmApiProtection;
}

export function shouldSetupMsal(): boolean {
  if (pageDataService.isWebView()) {
    // No MSAL support in WebView as we don't want to have MSAL fallback auth (not yet implemented)
    // Note that loginPopup won't work in iOS WebView due to WebKit configuration also
    // using loginRedirect may cause an issue with a page load as WebView initializaion may fail
    return false;
  }

  return pageDataService.hasActiveSubscription() && !!pageDataService.getFlightSetting().cmAuthCheckWeb;
}

export function isCreditScoreToggleEnabled(): boolean {
  return !!pageDataService.getFlightSetting().creditScoreVisibility;
}

export function shoulShowDownloadDefenderModalOnDashboard(): boolean {
  const skipDownloadDefenderModal = localStorageService.getDownloadDefenderModalSkipToDashboard();
  return !isITPSupported() && !skipDownloadDefenderModal;
}

export function isUnknownBreachOptimizationsEnabled(): boolean {
  return !!pageDataService.getFlightSetting().unknownBreachOptimizations;
}

export function isUseDedupedRecommendationsEnabled(): boolean {
  return !!pageDataService.getFlightSetting().useDedupedRecommendations;
}

export function shouldRedirectPaywallToOTS(): boolean {
  const scanStatus = pageDataService.getOneTimeCyberAgentScanAppStatusData();
  return Boolean(
    !!pageDataService.getFlightSetting().redirectPaywallToOTS && isOneTimeScanEnabled() && scanStatus?.isRegistrationAvailable
  );
}

export function isAsyncBreachScanEnabled(): boolean {
  return !!pageDataService.getFlightSetting().enableAsyncBreachScan;
}

export function isItmE2EEncryptionEnabled(): boolean {
  const localStorageEntry = localStorageService.getItem('itmE2EEncryptionEnabled');
  if (localStorageEntry === 'true') {
    return true;
  }
  if (localStorageEntry === 'false') {
    return false;
  }

  return !!pageDataService.getFlightSetting().identityMonitoringE2EEncryption;
}

export function isItmE2EEncryptionEnabledOnMac(): boolean {
  return !!pageDataService.getFlightSetting().identityMonitoringE2EEncryptionOnMac;
}

export function isItmDeleteWithHashEnabled(): boolean {
  return !!pageDataService.getFlightSetting().itmDeleteWithHash;
}

export function isThemeSwitchingWebEnabled(): boolean {
  return !!pageDataService.getFlightSetting().themeSwitchingWeb;
}

export function isThemeSwitchingWebViewEnabled(): boolean {
  return !!pageDataService.getFlightSetting().themeSwitchingWebView;
}

export function getUpsellRewardsWelcomeVariant(): string | undefined {
  return pageDataService.getFlightSetting().upsellRewardsWelcomeVariant;
}

export function isDiagnosticsPageEnabled(): boolean {
  return !!pageDataService.getFlightSetting().enableDiagnosticsPage;
}

export function isAppLauncherPageEnabled(): boolean {
  return !!pageDataService.getFlightSetting().appLauncherPage;
}
